import React, {
    useState,
    useRef,
    useLayoutEffect,
    useCallback,
    useMemo,
    useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import { Modal, notification } from 'antd';
import {useTranslation} from 'react-i18next'
import CommentBlock from 'src/components/comment-block';
import FilterByProfile from 'src/components/filter-by-profile';
import CommentEditor from 'src/components/comment-editor';
import CenterLoading from 'src/components/center-loading';
import NoRegisterMessage from 'src/components/no-register-message';
import { SEARCH_INTERVAL } from 'src/utils/constants.json';
import { noop } from 'lodash';
import './style.scss';

/**
 * Renders a comment modal component.
 * @param {Object} props - The component props.
 * @param {Object} props.deallocation - The deallocation object.
 * @param {Function} props.onClose - The function to close the modal.
 * @param {Array} props.profileOptions - The profile options.
 * @param {Function} props.onGetComments - The function to get comments.
 * @param {Function} props.onCreateComment - The function to create a comment.
 * @param {Function} props.onEditComment - The function to edit a comment.
 * @param {Function} props.onDeleteComment - The function to delete a comment.
 * @returns {JSX.Element} The rendered comment modal component.
 */
function CommentModal({ deallocation, onClose, profileOptions, onGetComments, onCreateComment, onEditComment, onDeleteComment }) {
    const commentContainer = useRef();
    const { sessionUser } = useSelector((store) => store.login);
    const [profile, setProfile] = useState('');
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [currentComment, setCurrentComment] = useState({});
    const [editing, setEditing] = useState(false);
    const [commentsCounter, setCommentCounter] = useState(0);

    const { t } = useTranslation();
    
    /**
     * Get all deallocation comments.
     */
    const getComments = useCallback(
        async function () {
            setLoading(true);
            try {
                const result = await onGetComments(deallocation.id);
                setComments(result.data.result);
            } catch(e) {
                // notification.error({message: t("errors.deallocations.comments.get")});
            }

            setLoading(false);
        },
        [setLoading, setComments, deallocation, onGetComments, t]
    );
        
    useEffect(() => {
        /**
         * Fetch comments' data upon changes.
         */
        getComments();
    }, [commentsCounter]);

    useLayoutEffect(() => { 
        /**
         * Sets up a layout effect to scroll the comment container to the bottom when the comments change.
         */
        const container = commentContainer.current;
        if (container) {
            setTimeout(() => {
                container.scrollTop = container.scrollHeight;
            }, 0);
        }
    }, [comments]);

    /**
     * Upon sending comment, decide whether to create or edit.
     */
    const onSendMessage = () => {
        if (editing) {
            const comment = {
                id: currentComment.id,
                deallocationId: deallocation.id,
                comment: currentComment.message,
            };
            editComment(comment);
        } else {
            const comment = {
                deallocationId: deallocation.id,
                comment: currentComment.message,
            };
            addComment(comment);
        }
    };

    /**
     * Create a comment.
     * @param {{}} comment - Comment object.
     */
    const addComment = async (comment) => {
        setSending(true);
        try {
            await onCreateComment(comment);

            setCommentCounter(comments.length+1);
            deallocation.hasComments = true;

            notification.success({message: t("success.deallocations.comments.create")});
        } catch(e) {
            notification.error({message: t("errors.deallocations.comments.create")});
        }
        
        setCurrentComment({});
        setSending(false);
    }

    /**
     * Set current editing comment.
     * @param {{}} comment - Comment object.
     */
    const onEdit = (comment) => {
        setCurrentComment({
            id: comment.id,
            message: comment.comment,
        });
        setEditing(true);
    };

    /**
     * Set editing object to an empty object upon canceling edit.
     */
    const stopEdit = () => {
        setEditing(false);
        setCurrentComment({});
    };

    /**
     * Edit a comment.
     * @param {{}} comment - Comment object.
     */
    const editComment = async (comment) => {
        setSending(true);
        try {
            await onEditComment(comment);
            await getComments();
            notification.success({message: t("success.deallocations.comments.edit")});
        } catch(e) {
            // notification.error({message: t("errors.deallocations.comments.edit")});
        }
        setSending(false);
        setCurrentComment({});
        setEditing(false);
    }

    /**
     * Request confirmation and delete a comment.
     * @param {{}} comment - Comment object.
     */
    const onDelete = (comment) => {
        Modal.confirm({
            title: t('deleteCommentConfirmTitle'),
            content: t('deleteCommentConfirmContent'),
            okText: t('delete'),
            okType: 'danger',
            cancelText: t('cancel'),
            onOk: async () => {
                try {
                    await onDeleteComment(comment.id);
                    await getComments();
                    notification.success({message: t("success.deallocations.comments.delete")});
                } catch(e) {
                    notification.error({message: t("errors.deallocations.comments.delete")});
                }
            },
        });
    };

    /**
     * Filter comments by profile and sort them by date.
     */
    const filteredComments = useMemo(() => {
        let data =
      profile !== ''
          ? comments.filter(
              (item) => profile === item.responsible_deallocation_comments.profile
          )
          : comments;

        data.sort(
            (item1, item2) => new Date(item1.createdAt) - new Date(item2.createdAt)
        );

        return data;
    }, [profile, comments]);

    const searchTimeout = useRef(null);

    /**
     * Handle filter changes.
     */
    const handleSetFilter = useCallback(
        (value) => {
            if (searchTimeout.current) {
                clearTimeout(searchTimeout.current);
            }

            searchTimeout.current = setTimeout(() => {
                setProfile(value);
                searchTimeout.current = null;
            }, SEARCH_INTERVAL);
        },
        [setProfile]
    );

    const isBlockedFromCommenting = deallocation.inactivationReason;

    return (
        <Modal
            width={1000}
            title={t('comments')}
            className="comment-modal"
            footer={null}
            onCancel={onClose}
            visible={true}
        >
            <div className="comments-container" ref={commentContainer}>
                {loading && <CenterLoading />}
                {!loading &&
          filteredComments.map((comment) => (
              <CommentBlock
                  key={comment.id}
                  message={comment.comment}
                  isEdited={comment.isEdited}
                  sender={
                      comment.responsible_deallocation_comments.email.split('@')[0]
                  }
                  profile={comment.responsible_deallocation_comments.profile}
                  date={comment.createdAt}
                  isOwner={
                      !isBlockedFromCommenting &&
                      comment.responsible_deallocation_comments.email === sessionUser.email
                  }
                  onEdit={() => onEdit(comment)}
                  onDelete={() => onDelete(comment)}
                  role='comment'
                  tabIndex={0}
              />
          ))}
                {!loading && comments.length === 0 && (
                    <NoRegisterMessage>{t('noCommentsAvailable')}</NoRegisterMessage>
                )}
            </div>
            <div aria-label={t('filterByProfile')} tabIndex={0}>
                <FilterByProfile
                    onSearch={handleSetFilter}
                    profileOptions={profileOptions}
                />
            </div>
            

            {!isBlockedFromCommenting && (
                <div className="editor-container">
                    <div aria-label={t('commentTextfield')} tabIndex={0}>{t('message400')}:</div>
                    <CommentEditor
                        value={currentComment}
                        onSend={onSendMessage}
                        loading={sending}
                        onChange={setCurrentComment}
                        isEditing={editing}
                        stopEditing={stopEdit}
                    />
                </div>
            )}
        </Modal>
    );
}

CommentModal.defaultProps = {
    deallocation: {},
    onClose: noop,
    profileOptions: {},
    onGetComments: noop,
    onCreateComment: noop,
    onEditComment: noop,
    onDeleteComment: noop,
};

export default CommentModal;
